import { Injectable } from '@angular/core';
import { API_ENDPOINT, GLOBAL_DATA_KEY } from '@core/configs';
import { ApiService } from '@core/services/common';
import { GlobalDataService } from '../common/global-data.service';
import { lastValueFrom } from 'rxjs';
import { ScreenTitleConfigRequestModel } from '@core/models/request/screen-title-config.model';
import { OfficeTypeRequestModel } from '@core/models/request';
import { AnswerTagRequest, TitleTagRequest } from '@core/models/request/title-tag-request.model';

@Injectable({
  providedIn: 'root',
})
export class ScreenTitleConfigService {
  constructor(
    private readonly http: ApiService,
    private readonly globalDataService: GlobalDataService
  ) {
  }

  async save(jsonData: string, screenCd: string, extraConfig: string, isUpdate: boolean) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const url = `${API_ENDPOINT.screenTitleConfig.save}`;
    const pk = `corp_${corpPrefix}#office_${officeCd}`;
    const body = new ScreenTitleConfigRequestModel(pk, screenCd, jsonData, extraConfig);
    body.CorpCd= this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);
    return await lastValueFrom(this.http.post(url, body));
  }

  async getByScreenCd(screenCd: string) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const officeType = this.globalDataService.get(GLOBAL_DATA_KEY.officeSelected)?.officeType;

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "ScreenCd": screenCd,
      "OfficeType":officeType
    };

    const url = `${API_ENDPOINT.screenTitleConfig.getByStaff}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async syncTitleMaster() {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
    };

    const url = `${API_ENDPOINT.screenTitleConfig.syncTitleMaster}`;
    try {
      const res = await lastValueFrom(this.http.post(url, body));
      return res;
    } catch (err: any) {
      return {status: err.status};
    }
  }

  async getTitleMaster() {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
    };

    const url = `${API_ENDPOINT.screenTitleConfig.getTitleMaster}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async checkMasterExist() {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const officeType = this.globalDataService.get(GLOBAL_DATA_KEY.officeSelected)?.officeType;
    const corpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "OfficeType": officeType,
      "CorpCd":corpCd
    };

    const url = `${API_ENDPOINT.screenTitleConfig.checkMasterExist}`;
    return await lastValueFrom(this.http.post(url, body));
  }

    async setToDefault(screenCd:string,filterColumns:string[]) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const officeType = this.globalDataService.get(GLOBAL_DATA_KEY.officeSelected)?.officeType;
    const corpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "OfficeType": officeType,
       "ScreenCd": screenCd,
       "CorpCd": corpCd,
       "FilterColumns":filterColumns
    };

    const url = `${API_ENDPOINT.screenTitleConfig.setTitleSettingToDefault}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async getTitleAfterLogin() {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
    };

    const url = `${API_ENDPOINT.screenTitleConfig.getTitleAfterLogin}`;
    return await lastValueFrom(this.http.post(url, body));
  }



  
  async updateCorpCd() {
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const corpPefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const partitionKey = `corp_${corpPefix}#office_${officeCd}`;
    const body = new OfficeTypeRequestModel(partitionKey, officeCd);
    const url = `${API_ENDPOINT.screenTitleConfig.updateCorpCd}`;
    return await lastValueFrom(this.http.post(url, body));
  }
  
  async getKbntagsSettings(displayNameParam:string[]) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const officeType = this.globalDataService.get(GLOBAL_DATA_KEY.officeSelected)?.officeType;
    const displayNames = displayNameParam;
    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "OfficeType": officeType,
      "DisplayNames": displayNames
    };

    const url = `${API_ENDPOINT.screenTitleConfig.getKbntagsSettings}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async getTitleByTags(titleTagRequest:TitleTagRequest) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const displayName = titleTagRequest.displayName;
    const tags = titleTagRequest.tags;
    
    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "DisplayName": displayName,
      "TtlTag":tags,
      "DocKbn":titleTagRequest.dockbn,
      "NoteKbn":titleTagRequest.notekbn
    };

    const url = `${API_ENDPOINT.screenTitleConfig.getTitleByTags}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async getAswerTags(titleTagRequest: AnswerTagRequest) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const displayName = titleTagRequest.displayName;
    const tag = JSON.parse(titleTagRequest.tag);
    
    const ttlKbn = titleTagRequest.ttlkbn;
    const ttlCd = titleTagRequest.ttlcd;
    const itemId = titleTagRequest.itemid;

    const body = {
        CorpPrefix: corpPrefix,
        OfficeCd: officeCd,
        DisplayName: displayName,
        TtlTag: tag,
        TtlKbn: ttlKbn ?? '',
        TtlCd: ttlCd ?? '',
        ItemId: itemId ?? ''
      };
    const url = `${API_ENDPOINT.screenTitleConfig.getAnswerByTags}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async getKbnTags() {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
    };

    const url = `${API_ENDPOINT.screenTitleConfig.getKbnTags}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async getTitletagSettingsByKbnTags(kbnTags:string[]) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const officeType = this.globalDataService.get(GLOBAL_DATA_KEY.officeSelected)?.officeType;
    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "OfficeType": officeType,
      "KbnTags": kbnTags
    };

    const url = `${API_ENDPOINT.screenTitleConfig.getTitletagSettingsByKbnTags}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async setToDefaultByColName(colName:string) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const officeType = this.globalDataService.get(GLOBAL_DATA_KEY.officeSelected)?.officeType;
    const corpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "OfficeType": officeType,
       "ScreenCd": "",
       "CorpCd": corpCd,
       "ColName": colName
    };

    const url = `${API_ENDPOINT.screenTitleConfig.setTagsDefaultByColName}`;
    return await lastValueFrom(this.http.post(url, body));
  }

    async getYoteisByTags(yoteiTagRequest:TitleTagRequest) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const displayName = yoteiTagRequest.displayName;
    const tags = yoteiTagRequest.tags;
    
    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "DisplayName": displayName,
      "TtlTag":tags,
      "DocKbn":yoteiTagRequest.dockbn,
      "NoteKbn":yoteiTagRequest.notekbn
    };

    const url = `${API_ENDPOINT.screenTitleConfig.getYoteisByTags}`;
    return await lastValueFrom(this.http.post(url, body));
  }

  async genYoteiToDefaultByColName(colName:string) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const officeType = this.globalDataService.get(GLOBAL_DATA_KEY.officeSelected)?.officeType;
    const corpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);

    const body = {
      "CorpPrefix": corpPrefix,
      "OfficeCd": officeCd,
      "OfficeType": officeType,
       "ScreenCd": "",
       "CorpCd": corpCd,
       "ColName": colName
    };

    const url = `${API_ENDPOINT.screenTitleConfig.genYoteiToDefaultByColName}`;
    return await lastValueFrom(this.http.post(url, body));
  }
}
